import type {
  AdminSessionInfo,
  Candidature,
  ServerStats,
} from '@asi-form/shared';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

/** Cache mémoire UI uniquement — source de vérité = cookie httpOnly via /auth/me */
let cachedAdmin: AdminSessionInfo | null = null;

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function redirectToLogin() {
  if (typeof window === 'undefined') return;
  if (!window.location.pathname.startsWith('/admin/login')) {
    window.location.assign('/admin/login');
  }
}

export function setAuthSession({ admin }: { admin?: { email?: string; name?: string } }) {
  cachedAdmin = {
    email: admin?.email,
    nom: admin?.name || admin?.email,
    loggedIn: true,
    timestamp: Date.now(),
  };
  try {
    localStorage.removeItem('adminSession');
    localStorage.removeItem('adminToken');
  } catch {
    // ignore
  }
}

export function clearAuthSession() {
  cachedAdmin = null;
  try {
    localStorage.removeItem('adminSession');
    localStorage.removeItem('adminToken');
  } catch {
    // ignore
  }
}

export function getAdminSession(): AdminSessionInfo | null {
  return cachedAdmin;
}

/** @deprecated cookies httpOnly */
export function getToken(): string | null {
  return cachedAdmin?.loggedIn ? 'cookie' : null;
}

async function apiFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const method = (options.method || 'GET').toUpperCase();
  const headers: Record<string, string> = {
    ...((options.headers as Record<string, string>) || {}),
  };

  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const csrf = readCookie('asi_csrf');
    if (!csrf) {
      throw new Error('Jeton CSRF manquant — reconnectez-vous');
    }
    headers['X-CSRF-Token'] = csrf;
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (response.status === 401) {
    clearAuthSession();
    redirectToLogin();
  }

  return response;
}

export async function loginAdmin(email: string, password: string) {
  const response = await fetch(`${API_BASE_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || 'Connexion impossible');
  }
  setAuthSession(data);
  return data;
}

export async function logoutAdmin() {
  try {
    await apiFetch('/auth/logout', { method: 'POST' });
  } catch {
    // ignore
  }
  clearAuthSession();
}

export async function fetchMe() {
  const response = await apiFetch('/auth/me');
  if (!response.ok) {
    throw new Error('Non authentifié');
  }
  const data = await response.json();
  setAuthSession(data);
  return data;
}

export async function getCandidatures(): Promise<Candidature[]> {
  const response = await apiFetch('/candidatures');
  if (!response.ok) {
    throw new Error('Erreur lors de la récupération des candidatures');
  }
  return response.json();
}

export async function getCandidatureById(id: string): Promise<Candidature> {
  const response = await apiFetch(`/candidatures/${id}`);
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.message || 'Candidature introuvable');
  }
  return response.json();
}

export async function createCandidature(
  candidatureData: Record<string, unknown>
): Promise<Candidature> {
  const response = await fetch(`${API_BASE_URL}/candidatures`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(candidatureData),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.message || 'Erreur lors de la création de la candidature');
  }
  return response.json();
}

export async function deleteCandidature(id: string) {
  const response = await apiFetch(`/candidatures/${id}`, { method: 'DELETE' });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.message || 'Erreur lors de la suppression de la candidature');
  }
  return response.json();
}

export async function updateCandidature(
  id: string,
  data: Record<string, unknown>
): Promise<Candidature> {
  const response = await apiFetch(`/candidatures/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Erreur lors de la mise à jour');
  }
  return response.json();
}

export async function getStats(): Promise<ServerStats> {
  const response = await apiFetch('/stats');
  if (!response.ok) {
    throw new Error('Erreur lors de la récupération des statistiques');
  }
  return response.json();
}

export async function importCandidatures(file: File) {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiFetch('/candidatures/import', {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Erreur lors de l'import des candidatures");
  }

  return response.json();
}
